// src/components/profile/ProfileItemGrid.tsx
//
// 3-column grid of a user's items on their profile page. Reads
// directly from /feed_items where owner_id == ownerUid; visibility
// rules drop items the viewer can't see.
//
// Why not from /users/{viewer}/feed: the materialized feed is
// time-based aggregation. Profile grids are owner-scoped and
// include items even before the viewer's feed materialized them
// (e.g. items from before the viewer followed). Source-of-truth
// query is correct here.

import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { FeedItem } from '../../feed/types';
import { useT } from '../../i18n/useT';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';

// Per-tile subcomponent so we can call useResolvedImageUrl per item
// (hooks can't be conditionally called inside the parent's .map()).
const Tile: React.FC<{ item: FeedItem }> = ({ item }) => {
  const resolved = useResolvedImageUrl(
    item.snapshot.preview_url || item.snapshot.image_path || null,
    // Pass item.id so non-owner profile viewers (anyone visiting
    // someone else's /u/{handle}) route through the
    // feedImageProxy. Owners viewing their own grid hit the
    // direct getDownloadURL path inside the resolver.
    item.id
  );
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        bgcolor: 'action.hover',
        overflow: 'hidden',
      }}
    >
      {resolved ? (
        <Box
          component="img"
          src={resolved}
          alt={item.snapshot.title || ''}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: 11,
              textAlign: 'center',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.snapshot.title || item.snapshot.caption || ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface Props {
  ownerUid: string;
  /** When true, viewer is the owner — show ALL items including
   *  private. When false, security rules already drop items the
   *  viewer can't see; we just render what comes back. */
  isSelf: boolean;
}

const GRID_LIMIT = 60;

export const ProfileItemGrid: React.FC<Props> = ({ ownerUid, isSelf }) => {
  const t = useT();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'feed_items'),
      where('owner_id', '==', ownerUid),
      orderBy('created_at', 'desc'),
      limit(GRID_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next: FeedItem[] = snap.docs.map((d) => {
          const data = d.data() as Partial<FeedItem>;
          return {
            id: d.id,
            owner_id: data.owner_id ?? '',
            author: data.author ?? { display_name: '' },
            kind: data.kind ?? ('photo' as any),
            visibility: data.visibility ?? 'private',
            created_at: (data.created_at ?? null) as Timestamp | null,
            updated_at: (data.updated_at ?? null) as Timestamp | null,
            snapshot: data.snapshot ?? {},
            source: data.source ?? { kind: 'manual', id: '' },
            reactions_count: data.reactions_count ?? 0,
            comments_count: data.comments_count ?? 0,
            reaction_target: data.reaction_target,
          };
        });
        setItems(next);
        setLoading(false);
      },
      (err) => {
        console.warn('ProfileItemGrid listener error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [ownerUid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
        <Typography variant="body2">
          {isSelf ? t('No posts yet.') : t('No posts to show.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 0.25,
        px: 0.25,
      }}
    >
      {items.map((item) => (
        <Tile key={item.id} item={item} />
      ))}
    </Box>
  );
};
